import { AlertTriangle, RotateCcw } from "lucide-react";

import {
  SkeletonAnnounce,
  SkeletonHeader,
  SkeletonTable,
  SkeletonTiles,
} from "@/components/dashboard/skeletons";
import { common, queryState } from "@/content";

/**
 * A whole screen while its primary read is in flight, and when that read fails.
 *
 * Used as an early return rather than a wrapper, and that is the point: after it,
 * the data is present, so the rest of the screen reads the way it did when the
 * server had already awaited it — no `data?.` threaded through two hundred lines
 * of JSX. A wrapper cannot give the compiler that.
 *
 * Screens whose primary datum is a list use `QueryState` instead: an empty table
 * with its filter bar still usable is better than replacing the page.
 */
export function PageLoading({ shape = "table" }: { shape?: "table" | "tiles" }) {
  return (
    <div className="mx-auto w-full max-w-console px-6 py-8">
      <SkeletonHeader />
      <div className="mt-8">{shape === "tiles" ? <SkeletonTiles /> : <SkeletonTable />}</div>
      <SkeletonAnnounce label={common.loading} />
    </div>
  );
}

export function PageError({
  error,
  onRetry,
}: {
  error?: Error | null;
  onRetry?: () => void;
}) {
  return (
    <div className="mx-auto w-full max-w-console px-6 py-8">
      <div
        role="alert"
        className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-5"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle
            aria-hidden
            className="mt-0.5 size-5 shrink-0 text-destructive"
            strokeWidth={1.6}
          />
          <div>
            <p className="text-sm font-bold text-n9">{queryState.errorTitle}</p>
            <p className="mt-1.5 max-w-[64ch] text-xs leading-snug text-n6">{queryState.errorLead}</p>
            {/* The API's own message. It is in Arabic and usually says exactly what
                went wrong, which beats a generic apology. */}
            {error?.message ? <p className="mt-2 text-2xs text-n7">{error.message}</p> : null}
          </div>
        </div>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-n4 bg-n0 px-3 text-xs font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
          >
            <RotateCcw aria-hidden className="size-3.5" strokeWidth={2} />
            {queryState.retry}
          </button>
        ) : null}
      </div>
    </div>
  );
}
